
import React, { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Plus, Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { OfficialHoliday } from "../types";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "@/components/ui/use-toast";

const HolidayManager: React.FC = () => {
  const { t } = useLanguage();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [holidayName, setHolidayName] = useState("");
  const [holidayDescription, setHolidayDescription] = useState("");
  const queryClient = useQueryClient();

  // Fetch holidays
  const { data: holidays, isLoading } = useQuery({
    queryKey: ['holidays'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('holidays')
        .select('*')
        .order('date', { ascending: true });
      
      if (error) {
        console.error('Error fetching holidays:', error);
        throw error;
      }
      
      return data || [];
    }
  });

  // Convert holidays to dates for the calendar
  const holidayDates = holidays?.map(h => new Date(h.date)) || [];

  // Add new holiday
  const addHoliday = useMutation({
    mutationFn: async (holiday: { name: string; date: string; description?: string }) => {
      const { data, error } = await supabase
        .from('holidays')
        .insert([
          {
            name: holiday.name,
            date: holiday.date,
            description: holiday.description,
            created_by: (await supabase.auth.getUser()).data.user?.id
          }
        ])
        .select();
      
      if (error) {
        console.error('Error adding holiday:', error);
        throw error;
      }
      
      return data[0];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holidays'] });
      setSelectedDate(undefined);
      setHolidayName("");
      setHolidayDescription("");
      toast({
        title: t("holidayAdded") || "Holiday Added",
        description: t("holidayAddedSuccess") || "The holiday has been successfully added.",
      });
    },
    onError: (error) => {
      console.error('Error adding holiday:', error);
      toast({
        variant: "destructive",
        title: t("error") || "Error",
        description: t("holidayAddError") || "Failed to add the holiday.",
      });
    }
  });

  // Delete holiday
  const deleteHoliday = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('holidays')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Error deleting holiday:', error);
        throw error;
      }
      
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holidays'] });
      toast({
        title: t("holidayDeleted") || "Holiday Deleted",
        description: t("holidayDeletedSuccess") || "The holiday has been successfully deleted.",
      });
    },
    onError: (error) => {
      console.error('Error deleting holiday:', error);
      toast({
        variant: "destructive",
        title: t("error") || "Error",
        description: t("holidayDeleteError") || "Failed to delete the holiday.",
      });
    }
  });

  const handleAddHoliday = () => {
    if (!selectedDate || !holidayName) {
      toast({
        variant: "destructive",
        title: t("validationError") || "Validation Error",
        description: t("provideDateAndName") || "Please provide both a date and name for the holiday.",
      });
      return;
    }

    addHoliday.mutate({
      name: holidayName,
      date: format(selectedDate, 'yyyy-MM-dd'),
      description: holidayDescription || undefined
    });
  };

  return (
    <Card className="w-full bg-background shadow-md rounded-lg border">
      <CardHeader className="pb-2 border-b">
        <CardTitle className="text-lg flex items-center gap-2">
          <CalendarIcon className="h-5 w-5 text-primary" />
          {t("manageHolidays") || "Manage Official Holidays"}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h3 className="font-medium text-foreground">{t("addNewHoliday") || "Add New Holiday"}</h3>
          
          <div className="grid gap-2">
            <Label htmlFor="date" className="text-foreground">{t("date") || "Date"}</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  className={`w-full justify-start text-left font-normal bg-background text-foreground border-input hover:bg-accent hover:text-accent-foreground ${!selectedDate ? "text-muted-foreground" : ""}`}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {selectedDate ? format(selectedDate, 'PPP') : t("selectDate") || "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[560px] max-w-[90vw] p-0 bg-popover border-border" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="holidayName" className="text-foreground">{t("holidayName") || "Holiday Name"}</Label>
            <Input
              id="holidayName"
              value={holidayName}
              onChange={(e) => setHolidayName(e.target.value)}
              placeholder={t("enterHolidayName") || "Enter holiday name"}
              className="bg-background text-foreground border-input placeholder:text-muted-foreground"
            />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="description" className="text-foreground">{t("description") || "Description"} ({t("optional") || "optional"})</Label>
            <Input
              id="description"
              value={holidayDescription}
              onChange={(e) => setHolidayDescription(e.target.value)}
              placeholder={t("enterDescription") || "Enter description"}
              className="bg-background text-foreground border-input placeholder:text-muted-foreground"
            />
          </div>
          
          <Button 
            onClick={handleAddHoliday} 
            disabled={!selectedDate || !holidayName || addHoliday.isPending}
            className="w-full"
          >
            <Plus className="mr-2 h-4 w-4" />
            {addHoliday.isPending 
              ? (t("adding") || "Adding...") 
              : (t("addHoliday") || "Add Holiday")}
          </Button>
        </div>
        
        <div>
          <h3 className="font-medium mb-4 text-foreground">{t("existingHolidays") || "Existing Holidays"}</h3>
          {isLoading ? (
            <div className="text-center py-4 text-muted-foreground">
              {t("loading") || "Loading..."}
            </div>
          ) : holidays && holidays.length > 0 ? (
            <ScrollArea className="h-[300px] pr-4">
              <div className="space-y-3">
                {holidays.map((holiday: OfficialHoliday) => (
                  <div key={holiday.id} className="flex items-center justify-between border border-border bg-card p-3 rounded-md">
                    <div>
                      <div className="font-medium text-foreground">{holiday.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {format(new Date(holiday.date), 'PPP')}
                      </div>
                      {holiday.description && (
                        <div className="text-sm mt-1 text-foreground">{holiday.description}</div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteHoliday.mutate(holiday.id)}
                      disabled={deleteHoliday.isPending}
                      className="hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-4 text-muted-foreground border border-border bg-card rounded-md">
              {t("noHolidays") || "No holidays defined yet."}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default HolidayManager;
